import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Mail, Pencil, Trash2, Send, Eye } from 'lucide-react'
import { emailApi, type EmailTemplate } from '../../api/endpoints'

import Button from '../../components/ui/Button'
import Modal from '../../components/ui/Modal'
import Input, { Select, Textarea } from '../../components/ui/Input'

import EmptyState from '../../components/ui/EmptyState'
import { useConfirm } from '../../components/ui/ConfirmDialog'
import { fmtDate } from '../../utils/date'

const CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: 'outreach', label: 'Outreach' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'proposal', label: 'Proposal' },
  { value: 'onboarding', label: 'Onboarding' },
]

export default function EmailPage() {
  const queryClient = useQueryClient()
  
  const [tab, setTab] = useState<'templates' | 'send'>('templates')
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<EmailTemplate | null>(null)
  const [previewHtml, setPreviewHtml] = useState<string | null>(null)

  const { data: templatesData, isLoading } = useQuery({
    queryKey: ['email-templates'],
    queryFn: emailApi.templates,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => emailApi.deleteTemplate(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['email-templates'] }),
  })

  const [confirmNode, confirm] = useConfirm()

  const templates = templatesData?.templates || []

  return (
    <div>
      {confirmNode}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Email</h1>
        <Button onClick={() => { setEditing(null); setModalOpen(true) }}>
          <Plus size={16} /> New Template
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit">
        {(['templates', 'send'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              tab === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t === 'templates' ? 'Templates' : 'Send Email'}
          </button>
        ))}
      </div>

      {tab === 'templates' && (
        isLoading ? (
          <div className="flex items-center justify-center h-64 text-gray-400">Loading...</div>
        ) : templates.length === 0 ? (
          <EmptyState icon={Mail} title="No templates" description="Create an email template to start sending" action={<Button onClick={() => { setEditing(null); setModalOpen(true) }}><Plus size={16} /> New Template</Button>} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((t) => (
              <div key={t.id} className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">{t.name}</h3>
                    {t.category && <span className="text-xs text-gray-500 capitalize">{t.category}</span>}
                  </div>
                  <div className="flex gap-0.5">
                    <button onClick={() => setPreviewHtml(t.body_html)} aria-label="Preview template" className="p-2.5 text-gray-400 hover:text-indigo-600 rounded"><Eye size={14} /></button>
                    <button onClick={() => { setEditing(t); setModalOpen(true) }} aria-label="Edit template" className="p-2.5 text-gray-400 hover:text-indigo-600 rounded"><Pencil size={14} /></button>
                    <button onClick={async () => { if (await confirm({ message: 'Are you sure you want to delete this template?', title: 'Delete Template', confirmLabel: 'Delete', confirmVariant: 'danger' })) deleteMutation.mutate(t.id) }} aria-label="Delete template" className="p-2.5 text-gray-400 hover:text-red-600 rounded"><Trash2 size={14} /></button>
                  </div>
                </div>
                <p className="text-xs text-gray-600 mb-3 line-clamp-2">Subject: {t.subject}</p>
                <div className="mt-auto text-xs text-gray-400">{fmtDate(t.created_at)}</div>
              </div>
            ))}
          </div>
        )
      )}

      {tab === 'send' && <SendEmailForm templates={templates} />}

      <TemplateFormModal open={modalOpen} onClose={() => { setModalOpen(false); setEditing(null) }} template={editing} />

      {/* Preview modal */}
      {previewHtml && (
        <Modal open={true} onClose={() => setPreviewHtml(null)} title="Template Preview" size="lg">
          <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: previewHtml }} />
        </Modal>
      )}
    </div>
  )
}

function SendEmailForm({ templates }: { templates: EmailTemplate[] }) {
  const [form, setForm] = useState({ lead_id: '', template_id: '', subject: '', html_body: '' })
  const [result, setResult] = useState<{ success: boolean; error?: string } | null>(null)

  const mutation = useMutation({
    mutationFn: (data: { lead_id: number; template_id?: number; subject?: string; html_body?: string }) => emailApi.send(data),
    onSuccess: (data) => setResult({ success: data.success }),
    onError: (err: Error) => setResult({ success: false, error: err.message }),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const data: any = { lead_id: parseInt(form.lead_id) }
    if (form.template_id) data.template_id = parseInt(form.template_id)
    if (form.subject) data.subject = form.subject
    if (form.html_body) data.html_body = form.html_body
    mutation.mutate(data)
  }

  const templateOptions = [{ value: '', label: 'No template (custom)' }, ...templates.map(t => ({ value: String(t.id), label: t.name }))]

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-2xl">
      <h2 className="text-lg font-semibold mb-4 flex items-center gap-2"><Send size={18} /> Send Email</h2>
      {result && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${result.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {result.success ? 'Email sent successfully!' : result.error || 'Failed to send'}
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input label="Lead ID *" type="number" value={form.lead_id} onChange={(e) => setForm(f => ({ ...f, lead_id: e.target.value }))} required />
        <Select label="Template" options={templateOptions} value={form.template_id} onChange={(e) => setForm(f => ({ ...f, template_id: e.target.value }))} />
        {!form.template_id && (
          <>
            <Input label="Subject *" value={form.subject} onChange={(e) => setForm(f => ({ ...f, subject: e.target.value }))} />
            <Textarea label="HTML Body *" value={form.html_body} onChange={(e) => setForm(f => ({ ...f, html_body: e.target.value }))} />
          </>
        )}
        <Button type="submit" loading={mutation.isPending}><Send size={14} /> Send Email</Button>
      </form>
    </div>
  )
}

function TemplateFormModal({ open, onClose, template }: { open: boolean; onClose: () => void; template: EmailTemplate | null }) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState({
    name: template?.name || '',
    subject: template?.subject || '',
    body_html: template?.body_html || '',
    body_text: template?.body_text || '',
    category: template?.category || '',
  })

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => template ? emailApi.updateTemplate(template.id, data) : emailApi.createTemplate(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['email-templates'] }); onClose() },
  })

  if (open && template && form.name !== template.name) {
    setForm({ name: template.name, subject: template.subject, body_html: template.body_html, body_text: template.body_text || '', category: template.category || '' })
  }

  return (
    <Modal open={open} onClose={onClose} title={template ? 'Edit Template' : 'New Template'} size="lg">
      <form onSubmit={(e) => { e.preventDefault(); const d = { ...form }; if (!d.body_text) delete (d as any).body_text; if (!d.category) delete (d as any).category; mutation.mutate(d) }} className="space-y-4">
        {mutation.error && <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">{(mutation.error as Error).message}</div>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input label="Template Name *" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} required />
          <Select label="Category" options={CATEGORY_OPTIONS} value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))} />
        </div>
        <Input label="Subject *" value={form.subject} onChange={(e) => setForm(f => ({ ...f, subject: e.target.value }))} required placeholder="Hi {{ lead_name }}, ..." />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">HTML Body *</label>
          <textarea
            value={form.body_html}
            onChange={(e) => setForm(f => ({ ...f, body_html: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-indigo-500"
            rows={8}
            required
            placeholder="<h1>Hello {{ lead_name }}</h1><p>...</p>"
          />
          <p className="text-xs text-gray-400 mt-1">Variables: {'{{ lead_name }}'}, {'{{ lead_company }}'}, {'{{ lead_email }}'}, {'{{ sender_name }}'}</p>
        </div>
        <Textarea label="Plain Text Body" value={form.body_text} onChange={(e) => setForm(f => ({ ...f, body_text: e.target.value }))} />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={mutation.isPending}>{template ? 'Save' : 'Create'}</Button>
        </div>
      </form>
    </Modal>
  )
}
